import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { formatDate } from '../../../utils/dateFormat';
import {
  serviceAgentIntroductionService,
  IntroducedInvestorDetail as Detail,
} from '../../../services/serviceAgentIntroduction.service';
import { getPermissionErrorMessage } from '../../../utils/apiClient';
import './IntroducedInvestorDetail.scss';

const Field: React.FC<{ label: string; value?: React.ReactNode; wide?: boolean }> = ({ label, value, wide }) => (
  <div className={`sa-intro-detail__field${wide ? ' sa-intro-detail__field--wide' : ''}`}>
    <div className="sa-intro-detail__label">{label}</div>
    <div className="sa-intro-detail__value">{value === null || value === undefined || value === '' ? <span className="text-muted">—</span> : value}</div>
  </div>
);

const stateBadge = (d: Detail) => {
  if (d.status === 'FAILED') return <span className="badge bg-danger">Failed</span>;
  if (d.status === 'DEACTIVATED') return <span className="badge bg-secondary">Deactivated</span>;
  if (d.registrationStatus === 'REGISTERED') return <span className="badge bg-success">Registered</span>;
  return <span className="badge bg-info text-dark">Pending registration</span>;
};

/**
 * "View details" for one introduction — the portal's read-only page, from our side.
 * Values come from Dataverse when reachable, so this is the record as it stands now.
 */
export const IntroducedInvestorDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [working, setWorking] = useState(false);

  const load = async () => {
    try {
      setDetail(await serviceAgentIntroductionService.getDetail(Number(id)));
    } catch (err: any) {
      toast.error(getPermissionErrorMessage(err) || err?.response?.data?.message || 'Failed to load the introduction');
      navigate('/service-agent/introduced-investors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const deactivate = async () => {
    if (!detail) return;
    setWorking(true);
    try {
      const after = await serviceAgentIntroductionService.deactivate(detail.id);
      setDetail(after);
      setConfirming(false);
      toast.success(`${after.code} has been deactivated`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not deactivate. The introduction is unchanged.');
    } finally {
      setWorking(false);
    }
  };

  if (loading || !detail) {
    return (
      <div className="sa-intro-detail facilon-dashboard-wrapper font-sans text-gray-800 min-h-screen pb-0">
        <main className="container-fluid px-0 pt-0 pb-0 dashboard-container-main">
          <div className="bg-gradient-to-r from-[#2c5e6a] to-[#355f69] text-white rounded shadow-sm mb-2 px-4 py-2.5 flex justify-between items-center flex-wrap gap-2">
            <div>
              <h1 className="text-[16px] font-bold text-white mb-0">Introduction details</h1>
              <p className="text-[12px] opacity-90 mt-1 mb-0">Reading the record from Power Apps…</p>
            </div>
            <button onClick={() => navigate('/service-agent/introduced-investors')}
                    className="bg-white/10 border border-white/25 rounded px-2.5 py-1 text-[11px] cursor-pointer hover:bg-white/20 transition-colors">
              <i className="bi bi-arrow-left me-1"></i> Back
            </button>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] mb-2"><div className="py-5 text-center"><Spinner animation="border" variant="primary" /></div></div>
        </main>
      </div>
    );
  }

  const fullName = [detail.firstName, detail.middleName, detail.lastName].filter(Boolean).join(' ');

  return (
    <div className="sa-intro-detail facilon-dashboard-wrapper font-sans text-gray-800 min-h-screen pb-0">
      <main className="container-fluid px-0 pt-0 pb-0 dashboard-container-main">

        <div className="bg-gradient-to-r from-[#2c5e6a] to-[#355f69] text-white rounded shadow-sm mb-2 px-4 py-2.5 flex justify-between items-center flex-wrap gap-2">
          <div>
            <h1 className="text-[16px] font-bold text-white mb-0">
              {fullName || detail.contactName || 'Introduced investor'}
              {detail.code && <span className="text-[12px] font-normal opacity-90 ml-2">{detail.code}</span>}
            </h1>
            <p className="text-[12px] opacity-90 mt-1 mb-0">
              Introduced for {detail.serviceProvider} &middot; created {detail.createdAt ? formatDate(detail.createdAt) : '—'}
            </p>
          </div>
          <div className="flex gap-2 items-center">
            {stateBadge(detail)}
            <button onClick={() => navigate('/service-agent/introduced-investors')}
                    className="bg-white/10 border border-white/25 rounded px-2.5 py-1 text-[11px] cursor-pointer hover:bg-white/20 transition-colors">
              <i className="bi bi-arrow-left me-1"></i> Back
            </button>
          </div>
        </div>

        {!detail.fromDataverse && detail.status !== 'FAILED' && (
          <div className="alert alert-warning py-2 px-3 text-[12px] mb-2">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Power Apps could not be reached, so this shows what was submitted rather than the live record.
          </div>
        )}
        {detail.status === 'FAILED' && (
          <div className="alert alert-danger py-2 px-3 text-[12px] mb-2">
            <i className="bi bi-x-circle me-2"></i>
            This introduction was not created in Power Apps.{detail.lastError ? ` ${detail.lastError}` : ''}
          </div>
        )}

        {/* Investor Management — mirrors the portal's View page section order */}
        <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] mb-2">
          <div className="sa-intro-detail__section-head">Investor Management</div>
          <div className="sa-intro-detail__grid">
            <Field label="Investor Type" value={detail.investorType} />
            <Field label="Nationality" value={detail.nationality} />
            <Field label="Country of Residence" value={detail.countryOfResidence} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] mb-2">
          <div className="sa-intro-detail__section-head">Investor Details</div>
          <div className="sa-intro-detail__grid">
            <Field label="Email" value={detail.email ? <a href={`mailto:${detail.email}`}>{detail.email}</a> : null} wide />
            <Field label="Country of Residence (ISD Code)" value={detail.isdCode} />
            <Field label="Mobile Phone" value={detail.mobile} />
            <Field label="Investor Name" value={fullName || detail.contactName} wide />
            <Field label="Use Facilon Service Agent?" value={detail.useFacilonServiceAgent ? 'Yes' : 'No'} />
            <Field label="Facilon Service Agent" value={detail.facilonServiceAgent} />
            <Field label="Client Executive Agent" value={detail.clientExecutiveAgent} />
            <Field label="Service Agent Status" value={detail.serviceAgentStatus} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] mb-2">
          <div className="sa-intro-detail__section-head">Investor Product</div>
          <div className="sa-intro-detail__grid">
            <Field label="Investment Route" value={detail.investmentRoute} />
            <Field label="Product" value={detail.product} wide />
            <Field label="Broker Preferred Bank" value={detail.brokerPreferredBank} />
            <Field label="Brokerage Plan" value={detail.brokeragePlan} wide />
            <Field label="Introduce Investor Product"
                   value={detail.journeyCode || (detail.status === 'CREATED'
                     ? <span className="fst-italic text-[#b45309]">creating…</span> : null)} />
          </div>
        </div>

        {/* Withdraw */}
        <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] px-3 py-2 mb-2 d-flex align-items-center flex-wrap gap-2">
          <div className="text-[11px] text-slate-500 me-auto" style={{ maxWidth: 560 }}>
            {detail.canDeactivate
              ? 'Deactivating withdraws this introduction: the record becomes inactive in Power Apps and the investor can no longer register from the invitation.'
              : detail.deactivateBlockedReason}
          </div>
          {detail.canDeactivate && !confirming && (
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setConfirming(true)}>
              <i className="bi bi-slash-circle me-1"></i> Deactivate
            </button>
          )}
          {confirming && (
            <>
              <span className="text-[12px] fw-semibold text-danger">Deactivate {detail.code}?</span>
              <button type="button" className="btn btn-sm btn-secondary" disabled={working} onClick={() => setConfirming(false)}>Keep it</button>
              <button type="button" className="btn btn-sm btn-danger" disabled={working} onClick={deactivate}>
                {working ? 'Deactivating…' : 'Yes, deactivate'}
              </button>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default IntroducedInvestorDetail;
